// LeadQualifierModal — Full-screen 1-question-per-screen guided assessment.
// Replaces the old 3-step modal. Same export signature so LeadProvider keeps working.
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  X,
  Check,
  ArrowRight,
  ArrowLeft,
  Shield,
  Lock,
  Phone,
  MapPin,
} from "lucide-react";
import { track } from "./Analytics";
import { goToShwcare } from "../../lib/shwcareHandoff";

const RED = "#E11D2A";
const BLACK = "#0A0A0A";

const INTERESTS = [
  { label: "Weight Loss",        sub: "GLP-1s · Compounded Tirzepatide" },
  { label: "Men's Health",       sub: "ED · Testosterone · Performance" },
  { label: "Women's Health",     sub: "Hormones · HRT · Wellness" },
  { label: "Peptides & Recovery",sub: "Sleep · Recovery · Longevity" },
  { label: "Hair Restoration",   sub: "Finasteride · Minoxidil · Topicals" },
  { label: "General Wellness",   sub: "Energy · Sleep · Vitality" },
];

const TRIED = [
  { label: "Yes",                 sub: "I've used GLP-1s or similar before" },
  { label: "No",                  sub: "This will be my first time" },
  { label: "Currently on one",    sub: "I'm transferring my care to BestRx" },
];

const GOALS = [
  { label: "Lose 20+ pounds",       sub: "Significant weight reduction" },
  { label: "Lose 10–20 pounds",     sub: "Slim down for a target date" },
  { label: "Increase energy",       sub: "Feel less tired day-to-day" },
  { label: "Improve performance",   sub: "Workout + recovery boost" },
  { label: "Hair restoration",      sub: "Slow loss · regrow density" },
  { label: "General wellness",      sub: "Overall health optimization" },
];

const TIMELINE = [
  { label: "ASAP",                  sub: "Start in the next 7 days" },
  { label: "Within 30 days",        sub: "Ready to begin this month" },
  { label: "Just exploring",        sub: "Learning my options" },
];

const apiBase = process.env.REACT_APP_BACKEND_URL;

function getUtm() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_medium: p.get("utm_medium") || "",
  };
}

function detectCityFromPath() {
  // /best/:city/:slug — primary city deep-link
  const m1 = window.location.pathname.match(/^\/best\/([a-z-]+)\/[a-z-]+/i);
  if (m1) return m1[1].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  // /locations/:city/...
  const m2 = window.location.pathname.match(/^\/locations\/([^/]+)/);
  if (m2) return m2[1].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return "";
}

export default function LeadQualifierModal({ open, onClose, context }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    city: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [recommendation, setRecommendation] = useState(null);

  // Build dynamic step list — interest is pre-filled if a med context exists,
  // we skip step 1 and seed the answer.
  const steps = useMemo(() => {
    const list = [];
    if (!context?.medSlug) list.push("interest");
    list.push("tried");
    list.push("goal");
    list.push("timeline");
    list.push("city");
    list.push("contact");
    return list;
  }, [context]);

  const reset = useCallback(() => {
    setStep(0);
    setAnswers({});
    setForm({ first_name: "", last_name: "", phone: "", email: "", city: "" });
    setSubmitting(false);
    setDone(false);
    setError("");
    setRecommendation(null);
  }, []);

  useEffect(() => {
    if (open) {
      // Seed interest if launched from a medication card
      if (context?.medName) {
        setAnswers((a) => ({ ...a, interest: context.medName }));
      }
      // Seed city from URL if available
      const c = detectCityFromPath();
      if (c) setForm((f) => ({ ...f, city: c }));
    } else {
      reset();
    }
  }, [open, context, reset]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const totalSteps = steps.length;
  const progress = done ? 1 : Math.min(step / totalSteps, 1);
  const stepKey = steps[step];

  const pickAnswer = (key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    setStep((s) => s + 1);
  };

  const back = () => {
    if (step === 0) { onClose(); return; }
    setStep((s) => s - 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const utm = getUtm();
      const cityFromPath = detectCityFromPath();
      const r = await fetch(`${apiBase}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          name: `${form.first_name} ${form.last_name}`.trim(),
          email: form.email,
          phone: form.phone,
          interest: answers.interest || context?.medName,
          tried_before: answers.tried,
          goal: answers.goal,
          source: context?.source || window.location.pathname,
          landing_page: window.location.href,
          campaign: utm.utm_campaign,
          utm_source: utm.utm_source,
          utm_medium: utm.utm_medium,
          city: form.city || cityFromPath || "",
          medication_slug: context?.medSlug || null,
          assessment_answers: {
            interest: answers.interest || context?.medName,
            tried_before: answers.tried,
            goal: answers.goal,
            timeline: answers.timeline,
            city: form.city || cityFromPath || "",
            strength: context?.medStrength || null,
          },
        }),
      });
      if (!r.ok) throw new Error("submit failed");
      setDone(true);
      // GA4 conversion event — the moment we have a confirmed lead.
      track("generate_lead", {
        interest: answers.interest || context?.medName,
        city: form.city || cityFromPath || "",
        timeline: answers.timeline,
        medication_slug: context?.medSlug || null,
        utm_source: utm.utm_source,
        utm_campaign: utm.utm_campaign,
      });
      try {
        const recResp = await fetch(`${apiBase}/api/recommendations/suggest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            interest: answers.interest || context?.medName,
            goal: answers.goal,
            tried_before: answers.tried,
            medication_slug: context?.medSlug || null,
            email: form.email,
          }),
        });
        if (recResp.ok) setRecommendation(await recResp.json());
      } catch (_e) { /* non-blocking */ }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Renderers ───
  const renderStep = () => {
    if (stepKey === "interest") {
      return (
        <QuestionScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          title="What are you interested in?"
          subtitle="Pick the area you'd like a provider to review."
          options={INTERESTS}
          onPick={(v) => pickAnswer("interest", v)}
          selected={answers.interest}
          testidPrefix="qa-interest"
        />
      );
    }
    if (stepKey === "tried") {
      return (
        <QuestionScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          title="Have you used these treatments before?"
          subtitle="There's no wrong answer — it just helps us match your protocol."
          options={TRIED}
          onPick={(v) => pickAnswer("tried", v)}
          selected={answers.tried}
          testidPrefix="qa-tried"
          twoCol
        />
      );
    }
    if (stepKey === "goal") {
      return (
        <QuestionScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          title="What is your main goal?"
          subtitle="Be honest — your provider tailors the dose to your goal."
          options={GOALS}
          onPick={(v) => pickAnswer("goal", v)}
          selected={answers.goal}
          testidPrefix="qa-goal"
        />
      );
    }
    if (stepKey === "timeline") {
      return (
        <QuestionScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          title="When do you want to start?"
          subtitle="We'll prioritize your provider review accordingly."
          options={TIMELINE}
          onPick={(v) => pickAnswer("timeline", v)}
          selected={answers.timeline}
          testidPrefix="qa-timeline"
          twoCol
        />
      );
    }
    if (stepKey === "city") {
      return (
        <CityScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          value={form.city}
          onChange={(v) => setForm((f) => ({ ...f, city: v }))}
          onNext={() => setStep((s) => s + 1)}
        />
      );
    }
    if (stepKey === "contact") {
      return (
        <ContactScreen
          eyebrow={`Step ${step + 1} of ${totalSteps}`}
          form={form}
          setForm={setForm}
          submit={submit}
          submitting={submitting}
          error={error}
        />
      );
    }
    return null;
  };

  return (
    <div
      data-testid="lead-qualifier-modal"
      className="fixed inset-0 z-[300] flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
    >
      {/* TOP BAR — progress + close */}
      <div className="border-b border-black/10 bg-white">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={back}
            aria-label={step === 0 ? "Close" : "Back"}
            data-testid={step === 0 ? "qa-close" : "qa-back"}
            className="inline-flex items-center gap-1.5 text-black/70 font-extrabold text-[12px] tracking-[0.1em] hover:text-black"
          >
            {step === 0 || done ? <X size={18} /> : <ArrowLeft size={18} />}
            <span className="hidden sm:inline">{step === 0 || done ? "CLOSE" : "BACK"}</span>
          </button>

          <div className="font-heavy text-[16px] sm:text-[20px] text-black leading-none">
            BEST<span style={{ color: RED }}>R</span>
            <span className="relative inline-block">x
              <span aria-hidden className="absolute left-0 right-0 top-1/2 h-[2px]"
                    style={{ background: BLACK, transform: "rotate(-12deg)" }} />
            </span>
          </div>

          <button
            onClick={onClose}
            aria-label="Close assessment"
            data-testid="qa-close-x"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-black/5 text-black/70"
          >
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-[3px] w-full bg-black/10">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progress * 100}%`, background: RED }}
            data-testid="qa-progress"
          />
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="qa-body">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-24">
          {done ? (
            <RecommendationPanel
              rec={recommendation}
              medName={context?.medName}
              medStrength={context?.medStrength}
              intent={answers.interest || context?.medName}
              firstName={form.first_name}
              onClose={onClose}
            />
          ) : (
            renderStep()
          )}
        </div>
      </div>

      {/* FOOTER trust strip */}
      {!done && (
        <div className="border-t border-black/10 bg-[#FAFAF8]">
          <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-3 flex flex-wrap items-center justify-center gap-4 sm:gap-7 text-black/70">
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[0.1em]">
              <Shield size={13} /> HIPAA SECURE
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[0.1em]">
              <Lock size={13} /> 100% PRIVATE
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[0.1em]">
              <Phone size={13} /> 30-MIN CALLBACK
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────  QUESTION SCREEN  ───────── */
function QuestionScreen({ eyebrow, title, subtitle, options, onPick, selected, testidPrefix, twoCol }) {
  return (
    <div className="qa-fade-in">
      <p
        className="text-[11px] font-extrabold tracking-[0.18em]"
        style={{ color: RED }}
      >
        {eyebrow}
      </p>
      <h2
        className="font-heavy text-black mt-2 leading-[1.05]"
        style={{ fontSize: "clamp(26px, 4vw, 44px)" }}
        data-testid={`${testidPrefix}-title`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-[14px] sm:text-[15px] text-black/65 leading-snug">
          {subtitle}
        </p>
      )}

      <div className={`mt-8 grid gap-3 ${twoCol ? "sm:grid-cols-2" : ""}`}>
        {options.map((o) => {
          const isObj = typeof o === "object";
          const label = isObj ? o.label : o;
          const sub = isObj ? o.sub : null;
          const isSel = selected === label;
          return (
            <button
              key={label}
              onClick={() => onPick(label)}
              data-testid={`${testidPrefix}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={
                "group text-left px-5 sm:px-6 py-4 sm:py-5 border-2 transition-all flex items-start gap-4 " +
                (isSel
                  ? "border-[color:var(--rx-red,#E11D2A)] bg-[#FFF5F6]"
                  : "border-black/12 bg-white hover:border-black/40")
              }
              style={isSel ? { borderColor: RED } : undefined}
            >
              <span
                className={
                  "shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border-2 transition-colors " +
                  (isSel ? "" : "border-black/20 group-hover:border-black/50")
                }
                style={isSel ? { borderColor: RED, background: RED } : undefined}
              >
                {isSel && <Check size={14} color="white" strokeWidth={4} />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-heavy text-[15px] sm:text-[17px] text-black uppercase tracking-[0.02em] leading-tight">
                  {label}
                </span>
                {sub && (
                  <span className="block text-[12px] sm:text-[13px] text-black/55 mt-1 leading-snug">
                    {sub}
                  </span>
                )}
              </span>
              <ArrowRight size={18} className="shrink-0 mt-1 text-black/30 group-hover:text-black transition-colors" strokeWidth={2.4} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────  CITY SCREEN  ───────── */
function CityScreen({ eyebrow, value, onChange, onNext }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    onNext();
  };
  return (
    <form onSubmit={handleSubmit} className="qa-fade-in" data-testid="qa-city-form">
      <p className="text-[11px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>
        {eyebrow}
      </p>
      <h2
        className="font-heavy text-black mt-2 leading-[1.05]"
        style={{ fontSize: "clamp(26px, 4vw, 44px)" }}
      >
        What city are you in?
      </h2>
      <p className="mt-3 text-[14px] sm:text-[15px] text-black/65 leading-snug">
        We'll match you with a provider licensed in your state.
      </p>

      <div className="mt-8 relative">
        <MapPin
          size={20}
          color={RED}
          className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
        />
        <input
          type="text"
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. Dallas, Houston, Austin…"
          data-testid="qa-city-input"
          className="w-full pl-12 pr-4 py-4 sm:py-5 bg-white border-2 border-black/15 focus:border-[color:var(--rx-red,#E11D2A)] outline-none text-[16px] sm:text-[18px] font-extrabold text-black placeholder:text-black/30 transition-colors"
          style={{ borderColor: value ? RED : undefined }}
        />
      </div>

      <button
        type="submit"
        disabled={!value.trim()}
        data-testid="qa-city-next"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-4 sm:py-5 font-heavy text-white text-[14px] sm:text-[16px] tracking-[0.1em] disabled:opacity-40 transition-opacity"
        style={{ background: RED, boxShadow: "0 10px 26px rgba(225,29,42,0.32)" }}
      >
        CONTINUE <ArrowRight size={18} strokeWidth={2.6} />
      </button>
    </form>
  );
}

/* ─────────  CONTACT SCREEN  ───────── */
function ContactScreen({ eyebrow, form, setForm, submit, submitting, error }) {
  return (
    <form onSubmit={submit} className="qa-fade-in" data-testid="qa-contact-form">
      <p className="text-[11px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>
        {eyebrow}
      </p>
      <h2
        className="font-heavy text-black mt-2 leading-[1.05]"
        style={{ fontSize: "clamp(26px, 4vw, 44px)" }}
      >
        Where should we reach you?
      </h2>
      <p className="mt-3 text-[14px] sm:text-[15px] text-black/65 leading-snug">
        A BestRx specialist will call you within <span className="font-extrabold text-black">30 minutes</span> during business hours.
      </p>

      <div className="mt-7 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="FIRST NAME"
            value={form.first_name}
            onChange={(v) => setForm({ ...form, first_name: v })}
            testid="qa-first-name"
            autoFocus
            required
          />
          <Field
            label="LAST NAME"
            value={form.last_name}
            onChange={(v) => setForm({ ...form, last_name: v })}
            testid="qa-last-name"
            required
          />
        </div>
        <Field
          label="PHONE NUMBER"
          type="tel"
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          testid="qa-phone"
          required
        />
        <Field
          label="EMAIL ADDRESS"
          type="email"
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          testid="qa-email"
          required
        />
      </div>

      {error && (
        <p className="mt-4 text-[13px] font-extrabold" style={{ color: RED }} data-testid="qa-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="qa-submit"
        className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 sm:py-5 font-heavy text-white text-[14px] sm:text-[16px] tracking-[0.1em] disabled:opacity-60 transition-opacity"
        style={{ background: RED, boxShadow: "0 10px 26px rgba(225,29,42,0.32)" }}
      >
        {submitting ? "SUBMITTING…" : <>GET MY MATCH <ArrowRight size={18} strokeWidth={2.6} /></>}
      </button>

      <p className="mt-4 text-[11px] text-black/50 leading-snug">
        By submitting, you agree to receive call &amp; SMS follow-up from BestRx. No spam.
        Your information is encrypted and HIPAA secure.
      </p>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, autoFocus }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] font-extrabold tracking-[0.16em] text-black/55 mb-1.5">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoFocus={autoFocus}
        data-testid={testid}
        className="w-full px-4 py-3.5 bg-white border-2 border-black/15 focus:border-[color:var(--rx-red,#E11D2A)] outline-none text-[15px] sm:text-[16px] font-extrabold text-black placeholder:text-black/30 transition-colors"
        style={{ borderColor: value ? RED : undefined }}
      />
    </label>
  );
}

/* ─────────  RECOMMENDATION PANEL (post-assessment)  ───────── */
function RecommendationPanel({ rec, medName, medStrength, intent, firstName, onClose }) {
  if (!rec) {
    return (
      <div className="qa-fade-in" data-testid="lead-recommendation-loading">
        <p className="text-[11px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>
          YOU'RE MATCHED
        </p>
        <h2 className="font-heavy text-black mt-2 leading-[1.05]" style={{ fontSize: "clamp(28px, 4.4vw, 48px)" }}>
          {firstName ? `${firstName.toUpperCase()}, ` : ""}YOUR PLAN IS BEING BUILT…
        </h2>
        <p className="mt-4 text-[14px] text-black/65">Building your personalized recommendation…</p>
        <div className="mt-8 h-1 w-full bg-black/10 overflow-hidden">
          <div className="qa-loader-bar h-full" style={{ background: RED }} />
        </div>
      </div>
    );
  }
  const { primary, alternatives = [], bundle } = rec;
  const addOn = alternatives.find((a) => a.category !== primary?.category) || alternatives[1] || alternatives[0];
  const altMed = alternatives[0] && alternatives[0].slug !== (addOn?.slug) ? alternatives[0] : alternatives[1];
  const startingPrice = primary?.promo_price_cents ?? primary?.starting_price_cents ?? 0;

  return (
    <div className="qa-fade-in" data-testid="lead-recommendation-panel">
      <p className="text-[11px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>
        YOU'RE MATCHED
      </p>
      <h2 className="font-heavy text-black mt-2 leading-[1.0]" style={{ fontSize: "clamp(28px, 4.4vw, 48px)" }}>
        {firstName ? `${firstName.toUpperCase()}, ` : ""}HERE'S YOUR PLAN.
      </h2>
      <p className="mt-3 text-[14px] text-black/65">
        A BestRx specialist will call within <span className="font-extrabold text-black">30 minutes</span>. Final dose set by your provider after intake.
      </p>

      {/* PRIMARY card */}
      <article
        className="mt-7 bg-white border-2 p-5"
        style={{ borderColor: RED, boxShadow: "0 16px 40px rgba(225,29,42,0.14)" }}
        data-testid="rec-primary"
      >
        <div className="flex items-start gap-4">
          {(primary?.product_image || primary?.image_url) && (
            <img
              src={primary.product_image || primary.image_url}
              alt={primary.name}
              className="w-24 h-24 sm:w-28 sm:h-28 object-contain bg-black/3 flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>PRIMARY</p>
            <p className="font-heavy text-[20px] sm:text-[24px] leading-tight text-black mt-0.5" data-testid="rec-primary-name">
              {primary.name?.toUpperCase()} {medStrength ? <span style={{ color: RED }}>{medStrength}</span> : null}
            </p>
            <p className="text-[13px] text-black/65 mt-1.5 leading-snug line-clamp-2">{primary.short_description}</p>
            <p className="font-heavy text-[22px] mt-3 text-black">
              ${Math.round(startingPrice / 100)}
              <span className="text-[12px] text-black/55 ml-1">/mo</span>
            </p>
          </div>
        </div>
        {primary.benefits?.length > 0 && (
          <ul className="mt-4 grid grid-cols-2 gap-1.5">
            {primary.benefits.slice(0, 4).map((b) => (
              <li key={b} className="text-[11px] tracking-[0.06em] uppercase text-black/70 font-extrabold flex items-start gap-1.5">
                <Check size={12} color={RED} strokeWidth={3.5} className="shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {rec.reason && (
          <p className="mt-4 pt-4 border-t border-black/10 text-[12px] italic text-black/65" data-testid="rec-reason">
            <span className="not-italic text-[10px] tracking-[0.16em] font-extrabold uppercase mr-1.5" style={{ color: RED }}>WHY</span>
            {rec.reason}
          </p>
        )}
      </article>

      {/* Alt + add-on */}
      {(altMed || addOn) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {altMed && (
            <article
              className="bg-white border border-black/12 p-4"
              data-testid={`rec-alternative-${altMed.slug}`}
            >
              <p className="text-[9.5px] font-extrabold tracking-[0.18em] text-black/55">ALTERNATIVE</p>
              <p className="font-heavy text-[15px] text-black leading-tight mt-1">{altMed.name}</p>
              <p className="text-[12px] text-black/60 mt-1 leading-snug line-clamp-2">{altMed.short_description}</p>
              <p className="font-heavy text-[15px] text-black mt-2">
                ${Math.round((altMed.starting_price_cents || 0) / 100)}
                <span className="text-[10px] text-black/55 ml-0.5">/mo</span>
              </p>
            </article>
          )}
          {addOn && addOn.slug !== altMed?.slug && (
            <article
              className="bg-white border border-black/12 p-4"
              data-testid={`rec-addon-${addOn.slug}`}
            >
              <p className="text-[9.5px] font-extrabold tracking-[0.18em] text-black/55">SUGGESTED ADD-ON</p>
              <p className="font-heavy text-[15px] text-black leading-tight mt-1">{addOn.name}</p>
              <p className="text-[12px] text-black/60 mt-1 leading-snug line-clamp-2">{addOn.short_description}</p>
              <p className="font-heavy text-[15px] text-black mt-2">
                ${Math.round((addOn.starting_price_cents || 0) / 100)}
                <span className="text-[10px] text-black/55 ml-0.5">/mo</span>
              </p>
            </article>
          )}
        </div>
      )}

      {/* Bundle */}
      {bundle && (
        <article
          className="mt-3 bg-black text-white p-5 border-2"
          style={{ borderColor: RED }}
          data-testid={`rec-bundle-${bundle.slug}`}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-extrabold tracking-[0.18em]" style={{ color: RED }}>BUNDLE &amp; SAVE</p>
            {bundle.savings_cents > 0 && (
              <span className="text-[10px] font-extrabold tracking-[0.16em]" style={{ color: RED }}>
                SAVE ${Math.round(bundle.savings_cents / 100)}
              </span>
            )}
          </div>
          <p className="font-heavy text-[18px] leading-tight">{bundle.name.toUpperCase()}</p>
          <p className="text-[12px] text-white/75 mt-1">{bundle.description}</p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {(bundle.medications || []).map((m) => (
              <span key={m.slug} className="text-[10px] tracking-[0.12em] uppercase font-extrabold border border-white/30 px-1.5 py-0.5">
                {m.name}
              </span>
            ))}
          </div>
          <p className="font-heavy text-[20px] mt-3">
            ${Math.round((bundle.bundle_price_cents || 0) / 100)}
            <span className="text-[11px] text-white/55 ml-1">/mo total</span>
          </p>
        </article>
      )}

      {/* CTAs — product-level handoff first: prefer the specific recommended/selected
          medication (slug or name) over a broad category so e.g. a Semaglutide
          selection resolves to the Semaglutide product, not a generic consult. */}
      <button
        onClick={() => goToShwcare(rec?.primary?.slug || medName || intent || rec?.primary?.category, { campaign: "bestrx_funnel_complete" })}
        data-testid="rec-cta-continue"
        className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 font-heavy text-white text-[14px] sm:text-[16px] tracking-[0.1em]"
        style={{ background: RED, boxShadow: "0 10px 26px rgba(225,29,42,0.32)" }}
      >
        CONTINUE TO PROVIDER REVIEW <ArrowRight size={18} strokeWidth={2.6} />
      </button>
      <a
        href="tel:+18447031780"
        data-testid="rec-cta-call"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 px-7 py-4 font-heavy text-black text-[14px] sm:text-[16px] tracking-[0.1em] bg-white border-2 border-black"
      >
        <Phone size={16} strokeWidth={2.6} /> SPEAK WITH OUR CARE TEAM
      </a>
      <p className="mt-4 text-[11px] text-black/50 text-center leading-snug">
        Final medication + dose is set by a licensed provider after intake.
      </p>
    </div>
  );
}
